import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { setTokens, clearTokens } from "@/lib/authInterceptor";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

function stashAndStripTokens(data) {
  // Save tokens (if any) to localStorage; never expose them on the user object
  if (data?.access_token || data?.refresh_token) {
    setTokens(data.access_token, data.refresh_token);
  }
  if (!data) return data;
  const { access_token, refresh_token, ...safe } = data;
  return safe;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  async function login(email, password) {
    const res = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    const safe = stashAndStripTokens(res.data);
    setUser(safe);
    return safe;
  }

  async function loginWithGoogle(sessionId) {
    const res = await axios.post(`${API}/api/auth/google-callback`, { session_id: sessionId }, { withCredentials: true });
    const safe = stashAndStripTokens(res.data);
    setUser(safe);
    return safe;
  }

  async function register(data) {
    const res = await axios.post(`${API}/api/auth/register`, data, { withCredentials: true });
    const safe = stashAndStripTokens(res.data);
    setUser(safe);
    return safe;
  }

  async function logout() {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    clearTokens();
    setUser(null);
  }

  async function updateProfile(data) {
    await axios.put(`${API}/api/auth/profile`, data, { withCredentials: true });
    setUser((prev) => ({ ...prev, ...data }));
  }

  async function changePassword(currentPassword, newPassword) {
    await axios.put(`${API}/api/auth/password`, { current_password: currentPassword, new_password: newPassword }, { withCredentials: true });
  }

  async function refreshUser() {
    try {
      const res = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  }

  function decrementTokens(type = "competency", amount = 1) {
    setUser((prev) => {
      if (!prev) return prev;
      const field = type === "assessment" ? "assessment_tokens" : "tokens";
      return { ...prev, [field]: Math.max(0, (prev[field] ?? 0) - amount) };
    });
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, register, logout, updateProfile, changePassword, setUser, refreshUser, decrementTokens }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
